import { useState, useEffect } from 'react';
import { useAuth } from './useAuth';

interface EngineeringUser {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  phoneNumber?: string;
  workspaceId: string;
  role: string;
  preferences: {
    notifications?: {
      email?: boolean;
      sms?: boolean;
      slack?: boolean;
      inApp?: boolean;
    };
    [key: string]: any;
  };
  metadata: Record<string, any>;
  isDemo: boolean;
}

export const useEngineeringAuth = () => {
  const demoAuth = useAuth();
  const [liveUser, setLiveUser] = useState<EngineeringUser | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'demo' | 'live'>('demo');

  const isDemoMode = (import.meta as any).env?.VITE_ENGINEERING_BRIDGE_DEMO === 'true';

  const currentUser = mode === 'demo' ? 
    (demoAuth.user ? {
      ...demoAuth.user,
      workspaceId: demoAuth.user.orgId,
      preferences: { notifications: { email: true, inApp: true } },
      metadata: {},
      isDemo: true
    } : null) : liveUser;

  useEffect(() => {
    const token = localStorage.getItem('engineering-auth-token');
    if (token && mode === 'live') {
      fetchUserProfile();
    }
  }, [mode]);

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('engineering-auth-token');
      const response = await fetch('/api/users/me', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setLiveUser({
          ...userData,
          isDemo: false
        });
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  const loginDemo = async (username: string, password: string) => {
    await demoAuth.login(username, password);
    setMode('demo');
  };

  const loginLive = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      if (!response.ok) throw new Error('Login failed');
      
      const { data } = await response.json();
      setLiveUser({
        ...data.user,
        isDemo: false
      });
      setMode('live');
      localStorage.setItem('engineering-auth-token', data.tokens.accessToken);
    } finally {
      setIsLoading(false);
    }
  };

  const registerLive = async (userData: any) => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });
      
      if (!response.ok) throw new Error('Registration failed');
      
      const { data } = await response.json();
      setLiveUser({
        ...data.user,
        isDemo: false
      });
      setMode('live');
      localStorage.setItem('engineering-auth-token', data.tokens.accessToken);
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    if (mode === 'demo') {
      demoAuth.logout();
    } else {
      setLiveUser(null);
      localStorage.removeItem('engineering-auth-token');
    }
  };

  const switchToLive = () => setMode('live');
  const switchToDemo = () => setMode('demo');

  const updateProfile = async (updates: Partial<EngineeringUser>) => {
    if (mode === 'demo') {
      const updated = { ...currentUser, ...updates };
      localStorage.setItem('engineering-demo-profile', JSON.stringify(updated));
      return updated;
    } else {
      try {
        const token = localStorage.getItem('engineering-auth-token');
        const response = await fetch('/api/users/me', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        });
        
        if (response.ok) {
          const updatedUser = await response.json();
          setLiveUser({ ...updatedUser, isDemo: false });
          return updatedUser;
        }
      } catch (error) {
        console.error('Failed to update profile:', error);
      }
    }
  };

  return {
    user: currentUser,
    isAuthenticated: !!currentUser,
    isLoading,
    mode,
    isDemoMode,
    loginDemo,
    loginLive,
    registerLive,
    logout,
    switchToLive,
    switchToDemo,
    updateProfile,
    hasRole: demoAuth.hasRole,
    getVisibleModules: demoAuth.getVisibleModules
  };
};
